import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import { useUserStore } from "@/stores/user";
import { bbsService } from "@/services/bbs/bbs";

// Layout
import DefaultLayout from "@/layouts/DefaultLayout.vue";
const lightweightPopupMeta = { skipGlobalDataRefresh: true };
const publicRouteMeta = { public: true };
const NOTICE_CENTER_ROOT_CATEGORY_ID = 25;
let noticeCategoryIdsRequest: Promise<Set<number>> | null = null;
const collectCategoryIds = (category: any, ids = new Set<number>()) => {
  if (!category || Number(category.onlyLoginIn) === 1) return ids;
  const id = Number(category.id);
  if (Number.isSafeInteger(id) && id > 0) ids.add(id);
  (category.children || []).forEach((child: any) => collectCategoryIds(child, ids));
  return ids;
};
const findCategory = (categories: any[], targetId: number): any => {
  for (const category of categories || []) {
    if (Number(category.id) === targetId) return category;
    const matched = findCategory(category.children || [], targetId);
    if (matched) return matched;
  }
};
const getNoticeCategoryIds = () => {
  if (!noticeCategoryIdsRequest) {
    noticeCategoryIdsRequest = bbsService.getCategoryTree()
      .then((categories) => {
        const root = findCategory(categories, NOTICE_CENTER_ROOT_CATEGORY_ID);
        return collectCategoryIds(root);
      })
      .catch((error) => {
        noticeCategoryIdsRequest = null;
        throw error;
      });
  }
  return noticeCategoryIdsRequest;
};
const withListQueryDefaults = (defaults: Record<string, string>) => (to: any) => {
  const query = { ...to.query };
  let changed = false;

  Object.entries(defaults).forEach(([key, defaultValue]) => {
    const currentValue = query[key];
    const invalidPage = key === "page" && (
      !Number.isInteger(Number(currentValue)) || Number(currentValue) < 1
    );
    if (currentValue === undefined || currentValue === null || invalidPage) {
      query[key] = defaultValue;
      changed = true;
    }
  });

  return changed ? { path: to.path, query, replace: true } : true;
};
const redirectLegacyNoticeList = async (to: any) => {
  const categoryId = Number(to.query.categoryId);
  if (!Number.isSafeInteger(categoryId) || categoryId <= 0) return true;

  try {
    const categoryIds = await getNoticeCategoryIds();
    if (!categoryIds.has(categoryId)) return true;
  } catch (error) {
    console.error("[Router] 获取公告分类失败，保留旧列表地址", error);
    return true;
  }

  const page = Number(to.query.page);
  return {
    path: "/notice-center",
    query: {
      categoryId: String(categoryId),
      page: String(Number.isInteger(page) && page > 0 ? page : 1),
      search: String(to.query.search || ""),
    },
    replace: true,
  };
};
const routes: RouteRecordRaw[] = [
  // ========== 有 Header 的页面 ==========
  {
    path: "/",
    component: DefaultLayout,
    children: [
      {
        path: "",
        component: () => import("@/pages/index/Index.vue"),
        meta: publicRouteMeta,
      },
      {
        path: "join",
        component: () => import("@/pages/auth/Join.vue"),
        meta: {
          ...publicRouteMeta,
          hideFloatingQuickMenu: true,
          hideFooter: true,
        },
      },
      {
        path: "bbs",
        component: () => import("@/pages/bbs/bbs.vue"),
        meta: publicRouteMeta,
      },
      {
        path: "nodata",
        component: () => import("@/pages/bbs/NoData.vue"),
        beforeEnter: withListQueryDefaults({ categoryId: "43", page: "1", search: "" }),
      },
      {
        path: "bbs/list",
        component: () => import("@/pages/bbs/boardList.vue"),
        meta: publicRouteMeta,
        beforeEnter: redirectLegacyNoticeList,
      },
      {
        path: "guide",
        component: () => import("@/pages/bbs/GuideList.vue"),
        meta: publicRouteMeta,
        beforeEnter: withListQueryDefaults({ categoryId: "22", page: "1", search: "" }),
      },
      {
        path: "notice-center",
        component: () => import("@/pages/bbs/NoticeCenter.vue"),
        meta: publicRouteMeta,
        beforeEnter: withListQueryDefaults({ categoryId: "26", page: "1", search: "" }),
      },
      {
        path: "guide/:id",
        component: () => import("@/pages/bbs/GuideDetail.vue"),
        meta: publicRouteMeta,
      },
      {
        path: "bbs/detail/:id",
        component: () => import("@/pages/bbs/BoardDetail.vue"),
        meta: publicRouteMeta,
      },
      {
        path: "policy/:type(terms|privacy)",
        component: () => import("@/pages/policy/PolicyDetail.vue"),
        meta: publicRouteMeta,
      },
      {
        path: "orders",
        component: () => import("@/pages/orderlist/OrderList.vue"),
        beforeEnter: withListQueryDefaults({ scope: "client_order_cn", page: "1", search: "" }),
      },
      {
        path: "customs-inquiry",
        component: () => import("@/pages/orderlist/customsInquiry.vue"),
      },
      {
        path: "delivery-application",
        component: () => import("@/pages/ordercreate/DeliveryApply.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "delivery-precautions",
        component: () => import("@/pages/ordercreate/DeliveryPrecautions.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "order-detail/:id",
        component: () => import("@/pages/orderviews/OrderDetail.vue"),
      },
      {
        path: "pay",
        component: () => import("@/pages/pay/PayTab.vue"),
        beforeEnter: withListQueryDefaults({ tab: "paymentWChina", page: "1", search: "" }),
      },
      {
        path: "point",
        component: () => import("@/pages/point/PointList.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "" }),
      },
      {
        path: "feedback",
        component: () => import("@/pages/feedback/FeedbackList.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "receive-address",
        component: () => import("@/pages/member/ReceiveAddress.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "" }),
      },
      {
        path: "deposit-ko",
        component: () => import("@/pages/deposit/DpstKo.vue"),
        beforeEnter: withListQueryDefaults({ tab: "DepositReq_1", page: "1", search: "" }),
      },
      {
        path: "deposit-cny",
        component: () => import("@/pages/deposit/DpstCNY.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "" }),
      },
      {
        path: "product-manage",
        component: () => import("@/pages/productmanage/ProductManageNew.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "", status: "1" }),
      },
      {
        path: "product-manage-old",
        component: () => import("@/pages/productmanage/index.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "", status: "1" }),
      },
      {
        path: "stock-pro",
        component: () => import("@/pages/stockpro/StockPro.vue"),
      },
      {
        path: "stock-manage",
        component: () => import("@/pages/stockmanage/index.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "" }),
      },
      {
        path: "inventory-outbound-statistics",
        redirect: {
          path: "/stock-manage",
          query: { tab: "outbound-statistics" },
        },
      },
      {
        path: "my-page",
        component: () => import("@/pages/mypage/MyPage.vue"),
      },
      {
        path: "api-settings",
        component: () => import("@/pages/ecommerce/ApiSettings.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "cooperation",
        component: () => import("@/pages/cooperation/Cooperation.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "" }),
      },
      {
        path: "coupons",
        component: () => import("@/pages/coupons/CouponList.vue"),
        beforeEnter: withListQueryDefaults({ tab: "coupon", page: "1", search: "" }),
      },
      {
        path: "notes",
        component: () => import("@/pages/notes/NoteList.vue"),
        beforeEnter: withListQueryDefaults({ page: "1", search: "" }),
      },
      {
        path: "bulk-excel",
        component: () => import("@/pages/excel/BulkExcel.vue"),
      },
      {
        path: "bulk-excel-result",
        component: () => import("@/pages/excel/BulkExcelResult.vue"),
      },

     
      {
        path: "sitemap",
        component: () => import("@/pages/sitemap/siteMap.vue"),
        meta: publicRouteMeta,
      },
      {
        path: "dlvr-mny",
        component: () => import("@/pages/member/DlvrMny.vue"),
      },
      {
        path: "feature-unavailable",
        component: () => import("@/pages/FeatureUnavailable.vue"),
        meta: publicRouteMeta,
      },
      {
        path: "compensation-deposit",
        component: () => import("@/pages/bonus/CompensationDeposit.vue"),
        meta: { requiresAuth: true },
        beforeEnter: withListQueryDefaults({ page: "1", search: "" }),
      },
    ],
  },

  // ========== 无 Header 的页面（登录、弹窗等） ==========
  {
    path: "/login",
    component: () => import("@/pages/auth/Login.vue"),
    meta: publicRouteMeta,
  },
  {
    path: "/oauth/kakao/callback",
    alias: ["/login/oauth/callback", "/oauth/callback", "/kakao/login/callback"],
    component: () => import("@/pages/auth/KakaoCallback.vue"),
    meta: publicRouteMeta,
  },
  {
    path: "/find-pass",
    component: () => import("@/pages/FindPass.vue"),
    meta: publicRouteMeta,
  },

  {
    path: "/trk-photo",
    component: () => import("@/pages/orderpopups/TrkPhotoPop.vue"),
  },
  {
    path: "/order-trk-photo/:id",
    component: () => import("@/pages/orderpopups/OrderTrkPhotoPop.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/order-bundle",
    component: () => import("@/pages/orderpopups/OrderBundlePopup.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/order-divide",
    component: () => import("@/pages/orderpopups/OrderDividePopup.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/order-rocket-file/:id",
    component: () => import("@/pages/orderpopups/OrderRocketPopup.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/pro-fetch",
    component: () => import("@/pages/orderpopups/proFetch.vue"),
  },
  {
    path: "/product-fetch",
    component: () => import("@/pages/orderpopups/GetProFetch.vue"),
  },
  {
    path: "/get-record-fetch",
    component: () => import("@/pages/orderpopups/RecordFetch.vue"),
  },
  {
    path: "/get-combined",
    component: () => import("@/pages/productmanage/GetCombined.vue"),
  },
  {
    path: "/get-order-records",
    component: () => import("@/pages/productmanage/GetOrderRecords.vue"),
  },
  {
    path: "/get-combined-records",
    component: () => import("@/pages/productmanage/GetCombinedRecords.vue"),
  },
  {
    path: "/get-product-url",
    component: () => import("@/pages/productmanage/GetProductUrl.vue"),
  },
  {
    path: "/ecommerce-product-sync",
    component: () => import("@/pages/productmanage/EcommerceProductSync.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/order-product-res",
    component: () => import("@/pages/productmanage/OrderProRes.vue"),
  },
  {
    path: "/order-combined-res",
    component: () => import("@/pages/productmanage/OrderProResCombined.vue"),
  },
  {
    path: "/package-req/:id",
    component: () => import("@/pages/orderpopups/packageReq.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/issue-handling/:id",
    component: () => import("@/pages/orderpopups/IssueHandling.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/order-subtrk/:id",
    component: () => import("@/pages/product/OrderProSubTrk.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/order-addr-edit/:id",
    component: () => import("@/pages/product/OrderAdrsPop.vue"),
  },

  {
    path: "/pro-add/:id",
    component: () => import("@/pages/product/OrderProMulitiPop.vue"),
  },
  {
    path: "/combine-qty-edit",
    component: () => import("@/pages/product/CombineQtyEditDialog.vue"),
  },
  {
    path: "/express-package/:id",
    component: () => import("@/pages/orderpopups/ExpressPackage.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/spu-modification",
    component: () => import("@/pages/productmanage/SpuModification.vue"),
  },
  {
    path: "/sku-modification",
    component: () => import("@/pages/productmanage/SkuModification.vue"),
  },
  {
    path: "/product-modification",
    component: () => import("@/pages/productmanage/proModification.vue"),
  },
  {
    path: "/service-detail",
    component: () => import("@/pages/product/ServiceDetailPopup.vue"),
  },
  {
    path: "/stock-in-out-details",
    component: () => import("@/pages/stockmanage/inOutDetails.vue"),
  },
  {
    path: "/stock-detail-preview",
    component: () => import("@/pages/stockmanage/StockDetailPreview.vue"),
  },
  {
    path: "/stock-sku-composition",
    component: () => import("@/pages/stockmanage/SkuCompositionDialog.vue"),
  },
  {
    path: "/storage-fee-export",
    component: () => import("@/pages/stockmanage/StorageFeeExportDialog.vue"),
  },
  {
    path: "/inventory-order-info",
    component: () => import("@/pages/inventoryoutbound/orderInfoDialog.vue"),
  },
  {
    path: "/pl-one-piece-order-detail-window",
    component: () => import("@/pages/orderviews/PlOnePieceOrderDetailWindow.vue"),
    meta: { ...lightweightPopupMeta, requiresAuth: true },
  },
  {
    path: "/additional-service-edit-window",
    component: () => import("@/pages/orderviews/AdditionalServiceEditWindow.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/inventory-sku-composition",
    component: () => import("@/pages/inventoryoutbound/SkuComposition.vue"),
  },
  {
    path: "/receive-address-form",
    component: () => import("@/components/Delivery/AddressFormDialog.vue"),
  },
  {
    path: "/customs-clearance-inquiry",
    component: () =>
      import("@/pages/orderpopups/CustomsClearanceInquiryDialog.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/truck-info",
    component: () => import("@/pages/orderpopups/TruckInfoDialog.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/mypage-edit-user",
    component: () => import("@/pages/mypage/EditUserDialog.vue"),
  },
  {
    path: "/mypage-korean-company",
    component: () => import("@/pages/mypage/KoreanCompanyDialog.vue"),
  },
  {
    path: "/return-order/:id",
    component: () => import("@/pages/orderpopups/ReturnOrder.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/problem-solve/:id",
    component: () => import("@/pages/orderpopups/problemInStock.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/purchase-cancel/:id",
    component: () => import("@/pages/orderpopups/ApplyPurchaseCancel.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/center-address",
    component: () => import("@/pages/member/CenterAddress.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/mem-addr",
    component: () => import("@/pages/member/memAddrPop.vue"),
  },
  {
    path: "/stock-select-modal",
    component: () => import("@/pages/stockpro/StockSelectModal.vue"),
  },
  {
    path: "/stock-pro-edit",
    component: () => import("@/pages/stockpro/StockProPopEdit.vue"),
  },
  {
    path: "/reg-select-modal",
    component: () => import("@/pages/regpro/RegSelectModal.vue"),
  },
  {
    path: "/history-order-product-select",
    component: () => import("@/pages/ordercreate/HistoryOrderProductSelect.vue"),
  },
  {
    path: "/order-fees",
    component: () => import("@/pages/orderviews/OrderFees.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/order-fees/:id",
    component: () => import("@/pages/orderviews/OrderFees.vue"),
    meta: lightweightPopupMeta,
  },
  {
    path: "/note-detail/:id",
    component: () => import("@/pages/notes/NoteDetail.vue"),
  },
  {
    path: "/available-coupons",
    component: () => import("@/pages/coupons/AvailableVoucherList.vue"),
  },
  {
    path: "/dlvr-calculation",
    component: () => import("@/pages/index/DlvrCalcuation.vue"),
    meta: publicRouteMeta,
  },
  {
    path: "/cooperation-settle-detail",
    component: () => import("@/pages/cooperation/CooperationSettleDetail.vue"),
  },
  {
    path: "/cooperation-bind-detail",
    component: () => import("@/pages/cooperation/CooperationBindDetail.vue"),
  },
  {
    path: "/cooperation-receipt",
    component: () => import("@/pages/cooperation/CooperationReceipt.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // 路由、分页、Tab 和普通链接跳转都不主动改变页面滚动位置。
    return false;
  },
});

router.beforeEach((to) => {
  const userStore = useUserStore();
  if (to.meta.public || userStore.userInfo?.token) return true;

  return { path: "/login", replace: true };
});

export default router;
